/**
 * WHAT TO CALL AN UPLOADED FILE ON SCREEN.
 *
 * Nothing in the database records the name a file arrived with — every caller stores the URL the
 * upload endpoint returns and nothing else — so every list of attachments in the app derives its
 * label from that URL. The stored name used to be `1789491443210-3f9a2b7c.jpg`, which is what the
 * reader saw: four slips on a payment, all named a different 13-digit number, none of them the name
 * of the file on the machine it came from.
 *
 * Uploads now keep a slug of their original name (`upload.module.ts`), so the label is the name back
 * again, minus the short id that makes it unique. Files stored before that change cannot be
 * recovered — their name was never written down — so they are called what they are: an attachment,
 * numbered where several sit together, with the type that decides how it opens.
 */

/** `1789491443210-3f9a2b7c` — a name from before uploads kept the original. */
const LEGACY_STEM = /^\d{12,14}-[0-9a-f]{8}$/i;
/** The uniqueness id this app appends to every stored name. */
const ID_TAIL = /-[0-9a-f]{8}$/i;

export interface FileLabelOptions {
  /** 1-based position, when several files are listed together and legacy ones must stay distinct. */
  index?: number;
  /** What an un-named file is called. */
  fallback?: string;
}

/** The file's name for a human: "gst-certificate.pdf", or "Attachment 2 (JPG)" for a legacy upload. */
export function fileLabel(url?: string | null, opts: FileLabelOptions = {}): string {
  const fallback = opts.fallback ?? 'Attachment';
  if (!url) return fallback;
  let base = String(url).split(/[?#]/)[0].split('/').pop() ?? '';
  try { base = decodeURIComponent(base); } catch { /* a stray % is not a reason to show nothing */ }
  if (!base) return fallback;

  const dot = base.lastIndexOf('.');
  const stem = dot > 0 ? base.slice(0, dot) : base;
  const ext = dot > 0 ? base.slice(dot + 1).toLowerCase() : '';

  if (LEGACY_STEM.test(stem)) {
    const n = opts.index ? ` ${opts.index}` : '';
    return `${fallback}${n}${ext ? ` (${ext.toUpperCase()})` : ''}`;
  }
  const clean = stem.replace(ID_TAIL, '');
  return `${clean || fallback}${ext ? `.${ext}` : ''}`;
}
